// ============================================================================
// OverlappingBoundaries Component (View)
// ============================================================================

import { Box, Typography, Alert } from "@mui/material";
import { AffectedJobsPanel, ConfirmDeleteDialog } from "../../components";
import JobMap from "../../../../../../helpers/JobMap";
import type { OverlappingBoundariesProps } from "./OverlappingBoundaries.types";
import { useOverlappingBoundariesViewModel } from "./OverlappingBoundaries.vm";
import "./OverlappingBoundaries.css";

/**
 * Component for displaying and resolving overlapping boundary issues
 */
export function OverlappingBoundaries(props: OverlappingBoundariesProps) {
	const vm = useOverlappingBoundariesViewModel(props);

	// No issues state
	if (!vm.hasIssues) {
		return (
			<Box data-testid="no-issues-message" className="overlapping-boundaries no-issues">
				<Alert severity="success">
					<Typography>No overlapping boundaries detected. You can proceed to the next step.</Typography>
				</Alert>
			</Box>
		);
	}

	// No current issue (shouldn't happen but handle gracefully)
	if (!vm.currentIssue) {
		return null;
	}

	return (
		<Box className="overlapping-boundaries">
			{/* Issue Description */}
			<Box className="issue-description">
				<Typography variant="body2" color="text.secondary">
					Ordered from largest to smallest overlap area.
				</Typography>
				<Typography variant="body2" color="text.secondary">
					These boundaries overlap by <strong style={{ color: "red" }}>{vm.intersectionAreaText}</strong>.
				</Typography>
			</Box>

			{/* Side-by-side layout: Content left, Map right */}
			<Box className="side-by-side-layout">
				{/* Left side - Job Legend */}
				<Box className="left-side">
					<AffectedJobsPanel
						jobs={vm.currentIssue.jobs}
						hiddenJobIds={vm.hiddenJobIds}
						onToggleVisibility={vm.toggleJobVisibility}
						onShowAll={vm.showAllJobs}
						onHideAll={vm.hideAllJobs}
						onDeleteJob={vm.handleDeleteJobClick}
						getJobSourceFiles={vm.getJobSourceFiles}
					/>
				</Box>

				{/* Right side - Map */}
				<Box className="right-side">
					<JobMap
						jobs={vm.currentIssue.jobs}
						colorMode="boundary-hatch"
						highlightGeometry={vm.currentIssue.intersectionGeometry}
						height="100%"
						hiddenJobIds={Array.from(vm.hiddenJobIds)}
					/>
				</Box>
			</Box>

			{/* Delete Confirmation Dialog */}
			<ConfirmDeleteDialog
				open={vm.deleteJobId !== null}
				title="Delete Job"
				message={`Are you sure you want to delete "${vm.getJobDisplayName(vm.deleteJobId || "")}"? This action cannot be undone.`}
				onConfirm={vm.handleConfirmDelete}
				onCancel={vm.handleCancelDelete}
			/>
		</Box>
	);
}
